/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import { useState } from 'react'
import { useServiceContext } from '../root'
import _ from 'lodash'
import { getById as getMetadataById } from '../services/api/metadata'

interface EntityProperties {
  [key: string]: any
}

interface LinkedEntitiesData {
  elements: any[]
  [key: string]: any
}

function useEntitySearchService() {
  const context = useServiceContext()
  const { api } = context
  const [properties, setProperties] = useState<EntityProperties | null>(null)

  const getLinkedEntities = async (data: LinkedEntitiesData) => {
    // Directly call getLinkedEntities (returns a promise)
    const dataEntitylinkToBeForwarded = await api.etmf.getLinkedEntities()
    const propData = await getPropertiesAndOptions([dataEntitylinkToBeForwarded])
    setProperties(propData)
    return getPropsValue(data, dataEntitylinkToBeForwarded)
  }

  async function getPropertiesAndOptions(results: any[]): Promise<EntityProperties> {
    const dataEntitylink = results[0]
    const properties: EntityProperties = {}
    properties['zone'] = { id: dataEntitylink.entityLinkMap.ZONE }
    properties['section'] = { id: dataEntitylink.entityLinkMap.SECTION }
    properties['milestone'] = { id: dataEntitylink.entityLinkMap.MILESTONE, usedValues: [] }
    properties['artifact'] = { id: dataEntitylink.entityLinkMap.ARTIFACT, usedValues: [] }
    const pVSearchPromises = Object.entries(properties)
      .filter(([_, prop]) => prop.id)
      .map(([key, prop]) => {
        // Use propertyValuesSearch for each property id
        return api.etmf.propertyValuesSearch(String(prop.id), {}, true).then((values: any[]) => {
          if (key === 'level') {
            // Remove duplicates and convert to uppercase for level values
            values = Array.from(new Set(values.map((item) => item.toUpperCase())))
          }
          prop.usedValues = values
        })
      })
    // Add artifact fetching if exists
    if (dataEntitylink.entityLinkMap.ARTIFACT) {
      if (!properties['artifact']) properties['artifact'] = {}
      const artifactPromise = getMetadataById(dataEntitylink.entityLinkMap.ARTIFACT).then((data: any) => {
        if (data && data.definition) {
          properties['artifact'].values = data.definition.options
        }
      })
      pVSearchPromises.push(artifactPromise)
    }
    await Promise.allSettled(pVSearchPromises)
    return properties
  }

  function getPropsValue(data: LinkedEntitiesData, dataEntitylink: any) {
    _.forEach(data.elements, function (val: any) {
      if (val.fsi && !val.fsiDetail) {
        val.fsiDetail = val.fsi
        val.name = val.fsi.name
      }
      try {
        const jsondata = val.jsonData ? JSON.parse(val.jsonData) : val.data ? JSON.parse(val.data) : {}
        const zone = jsondata[dataEntitylink.entityLinkMap.ZONE]
        const section = jsondata[dataEntitylink.entityLinkMap.SECTION]
        const milestone = jsondata[dataEntitylink.entityLinkMap.MILESTONE]
        val.zone = zone && zone.v ? zone.v : ''
        val.section = section && section.v ? section.v : ''
        val.milestone = milestone && milestone.v ? milestone.v : ''
      } catch (e) {
        console.log('error during file properties postprocessing', e)
      }
    })
    return data
  }

  return {
    getLinkedEntities,
    properties
  }
}

export default useEntitySearchService
